// The device's IndexedDB, as the plan's device diagram draws it: projects
// (the documents, with a revision), outbox (Changes waiting for sync, in
// order), media (bytes by content hash, immutable) and meta (the device id,
// `lastSeq` the last list sequence applied, `listId` the list the copy
// belongs to, and other small, never-synced values). Only store.ts opens it. The version goes up with the schema, and
// `upgrade` walks every older one forward. Signing out erases it
// (eraseStoreDb), so the next person on a shared device starts as a visitor.
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Change } from '@shared/edit';
import type { Id, Project } from '@shared/plan';

export interface ProjectRow { doc: Project; rev: number }

export interface StoreSchema extends DBSchema {
  projects: { key: Id; value: ProjectRow };
  outbox: { key: number; value: Change };
  media: { key: string; value: Blob };
  meta: { key: string; value: unknown };
}

export type StoreDb = IDBPDatabase<StoreSchema>;

export const DB_NAME = 'honey-do';
export const DB_VERSION = 1;

const STORES = ['projects', 'outbox', 'media', 'meta'] as const;

export function openStoreDb(name = DB_NAME): Promise<StoreDb> {
  const opening: Promise<StoreDb> = openDB<StoreSchema>(name, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        db.createObjectStore('projects');
        db.createObjectStore('outbox', { autoIncrement: true });
        db.createObjectStore('media');
        db.createObjectStore('meta');
      }
    },
    // Another tab is deleting the database (signing out) or upgrading it:
    // let go, so it is not left waiting on this one.
    blocking() {
      void opening.then((db) => db.close());
    },
  });
  return opening;
}

/**
 * Sign-out on this device: every store emptied in one transaction (so the
 * data is gone even if the delete has to wait), the connection closed, then
 * the database deleted. A delete another tab still blocks finishes when that
 * tab lets go; it is not waited for here.
 */
export async function eraseStoreDb(name = DB_NAME): Promise<void> {
  const db = await openStoreDb(name);
  try {
    const tx = db.transaction([...STORES], 'readwrite');
    await Promise.all([...STORES.map((store) => tx.objectStore(store).clear()), tx.done]);
  } finally {
    db.close();
  }
  await new Promise<void>((resolve) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}
